import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setCsrfToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionState, setSessionState] = useState(null); // ACTIVE | AWAITING_MFA | AWAITING_PW_CHANGE
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    setUser(null);
    setSessionState(null);
    setCsrfToken(null);
  }, []);

  const bootstrap = useCallback(async () => {
    // Try /me — will succeed if a valid cookie is present. R13: /me now
    // rotates and returns a fresh CSRF token so a refreshed / directly-opened
    // page can execute state-changing requests (POST/PUT/PATCH/DELETE)
    // without a full re-login. Token stays in module memory only.
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      setSessionState(r.data.session_state);
      if (r.data.csrf_token) {
        setCsrfToken(r.data.csrf_token);
      }
    } catch (_) {
      clear();
    } finally {
      setLoading(false);
    }
  }, [clear]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const login = useCallback(async (username, password) => {
    const r = await api.post("/auth/login", { username, password });
    setCsrfToken(r.data.csrf_token);
    setUser(r.data.user);
    setSessionState(r.data.session_state);
    return r.data;
  }, []);

  const verifyMfa = useCallback(async (code) => {
    const r = await api.post("/auth/mfa/verify", { code });
    setCsrfToken(r.data.csrf_token);
    setUser(r.data.user);
    setSessionState(r.data.session_state);
    return r.data;
  }, []);

  const changePassword = useCallback(async (oldPw, newPw, confirmPw) => {
    const r = await api.post("/auth/change-password", {
      old_password: oldPw,
      new_password: newPw,
      confirm_password: confirmPw ?? newPw,
    });
    // R6: server revokes all sessions on success — force full re-login.
    if (r?.data?.must_relogin) {
      clear();
    }
    return r.data;
  }, [clear]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore network errors on logout */ }
    clear();
  }, [clear]);

  const value = useMemo(() => ({
    user, sessionState, loading, login, verifyMfa, changePassword, logout,
  }), [user, sessionState, loading, login, verifyMfa, changePassword, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
